import {
  useInviteTeacherMutation,
  useResendSchoolInvitationMutation,
} from '@/types/vue-apollo-helpers';
import type { InviteTeacherMutationVariables } from '@/types/operations';

/**
 * Machine-readable reasons the backend refused a single invite. Mirrors
 * `App\Enums\InviteErrorCode` in sms-be — the codes travel on the GraphQL
 * error's `extensions.code`, which is not part of the schema and so has no
 * generated enum; keep the two lists in step.
 */
export const InviteErrorCode = {
  InvitationPending: 'INVITATION_PENDING',
  StaffExists: 'STAFF_EXISTS',
} as const;

export type InviteError = {
  /** One of `InviteErrorCode`, or null for anything the backend did not tag. */
  code: string | null;
  message: string;
  /** The invitation that blocked the invite — present only for InvitationPending. */
  invitationId: string | null;
};

type GraphQLErrorLike = {
  message?: string;
  extensions?: Record<string, unknown>;
};

function asString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function toInviteError(caught: unknown): InviteError {
  const apolloError = caught as { message?: string; graphQLErrors?: GraphQLErrorLike[] };
  const first = apolloError?.graphQLErrors?.[0];
  const extensions = first?.extensions ?? {};

  return {
    code: asString(extensions.code),
    message:
      first?.message
      || apolloError?.message
      || 'The invitation could not be sent. Please try again.',
    invitationId: asString(extensions.invitation_id),
  };
}

export function useInviteTeacher() {
  const { mutate: inviteMutate, loading: inviteLoading, onDone } = useInviteTeacherMutation();
  const { mutate: resendMutate, loading: resendLoading } = useResendSchoolInvitationMutation();

  const error = ref<InviteError | null>(null);

  // @vue/apollo-composable's `mutate` re-throws when no onError handler is
  // registered, so the old `await inviteTeacher(...)` in a click handler turned
  // every duplicate invite into an unhandled promise rejection (Sentry
  // JAVASCRIPT-VUE-H). Catching here keeps the failure on `error` where the
  // dialog can render it.
  let pending: Promise<unknown> | null = null;

  const inviteTeacher = (variables: InviteTeacherMutationVariables) => {
    // Assigned before the first await, so a second click (or Enter) during the
    // round trip joins the in-flight request instead of firing a second invite
    // — the guard #511 used for the duplicate academic calendar.
    if (pending) return pending;

    error.value = null;

    const request = (async() => {
      try {
        return await inviteMutate(variables);
      } catch (caught) {
        error.value = toInviteError(caught);
        return null;
      }
    })().finally(() => {
      pending = null;
    });

    pending = request;

    return request;
  };

  /**
   * Refresh the token on the invitation that blocked the last invite and mail
   * it again. Reuses the existing `resendSchoolInvitation` mutation — and its
   * permission check — rather than reopening the invite path, so no duplicate
   * row is ever created. Resolves true when the mail went out.
   */
  const resendInvitation = async(): Promise<boolean> => {
    const id = error.value?.invitationId;
    if (!id || resendLoading.value) return false;

    try {
      await resendMutate({ id });
      error.value = null;
      return true;
    } catch (caught) {
      // The invitation is still pending, so keep the code and the id: the
      // dialog goes on offering Resend, now explaining why it did not go out.
      error.value = {
        ...toInviteError(caught),
        code: InviteErrorCode.InvitationPending,
        invitationId: id,
      };
      return false;
    }
  };

  return {
    inviteTeacher,
    resendInvitation,
    loading: inviteLoading,
    resending: resendLoading,
    error,
    onDone,
  };
}
